import React from "react";
import { motion } from "framer-motion";

/**
 * AnimatedConnexisMark — the Connexis brand symbol with a hovering float,
 * pulsing halo and softly-blinking accent dots. Use anywhere we'd previously
 * have reached for a generic crown / sparkle. Built for hero / final-CTA
 * focal points on dark backgrounds.
 *
 * `size`: pixel size of the white card. Default 64.
 * `glow`: tone of the halo. Defaults to a Connexis blue.
 */
export default function AnimatedConnexisMark({ size = 64, glow = "#0055FF", className = "", testid = "connexis-mark" }) {
  const dotR = Math.max(2, Math.round(size * 0.066));
  const stroke = Math.max(1.8, size * 0.05);

  return (
    <div
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: size * 1.6, height: size * 1.6 }}
      data-testid={testid}
    >
      {/* Outer pulse rings — stacked, staggered */}
      <span
        className="absolute inset-0 m-auto rounded-2xl"
        style={{
          width: size,
          height: size,
          boxShadow: `0 0 0 0 ${glow}80`,
          animation: "cx-mark-pulse 2.8s ease-out infinite",
        }}
      />
      <span
        className="absolute inset-0 m-auto rounded-2xl"
        style={{
          width: size,
          height: size,
          boxShadow: `0 0 0 0 ${glow}55`,
          animation: "cx-mark-pulse 2.8s ease-out infinite",
          animationDelay: "1.4s",
        }}
      />

      {/* Static soft glow behind the card */}
      <span
        className="absolute inset-0 m-auto rounded-full blur-2xl"
        style={{
          width: size * 1.4,
          height: size * 1.4,
          background: `radial-gradient(circle, ${glow}55 0%, transparent 70%)`,
          opacity: 0.9,
        }}
      />

      {/* Hovering card */}
      <motion.div
        animate={{ y: [0, -4, 0] }}
        transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}
        className="relative inline-flex items-center justify-center bg-white text-slate-950 shadow-[0_12px_40px_-8px_rgba(255,255,255,0.45)]"
        style={{
          width: size,
          height: size,
          borderRadius: Math.round(size * 0.22),
        }}
      >
        {/* Inner shimmer — diagonal sheen that slowly drifts */}
        <span
          className="absolute inset-0 overflow-hidden"
          style={{ borderRadius: Math.round(size * 0.22) }}
          aria-hidden
        >
          <span
            className="absolute -inset-[40%]"
            style={{
              background: `linear-gradient(115deg, transparent 30%, ${glow}22 50%, transparent 70%)`,
              animation: "cx-mark-sheen 6s ease-in-out infinite",
            }}
          />
        </span>

        {/* Brand mark — same SVG as the footer, scaled */}
        <svg
          viewBox="0 0 24 24"
          width={Math.round(size * 0.58)}
          height={Math.round(size * 0.58)}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="relative"
          aria-label="Connexis"
        >
          <path d="M4 7h7M4 12h12M4 17h7" strokeLinecap="round" />
          <circle cx="18" cy="7"  r="2.4" fill={glow} stroke="none">
            <animate attributeName="opacity" values="1;0.55;1" dur="2.2s" repeatCount="indefinite" />
          </circle>
          <circle cx="18" cy="17" r="2.4" fill={glow} stroke="none">
            <animate attributeName="opacity" values="0.55;1;0.55" dur="2.2s" repeatCount="indefinite" />
          </circle>
        </svg>
      </motion.div>

      <style>{`
        @keyframes cx-mark-pulse {
          0%   { box-shadow: 0 0 0 0 ${glow}66; opacity: 0.9; }
          100% { box-shadow: 0 0 0 ${size * 0.6}px ${glow}00; opacity: 0; }
        }
        @keyframes cx-mark-sheen {
          0%, 100% { transform: translateX(-40%); opacity: 0; }
          50%      { transform: translateX(40%);  opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="${"connexis-mark"}"] *,
          [data-testid="${"connexis-mark"}"] {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
